// code-guardian-ignore-duplicates: anchor-click download variants are kept byte-for-byte as their call sites had them (body-appended anchor here, detached anchor in utils/csv.ts); merging them would change which DOM path an export takes.
// Browser file-download helpers extracted verbatim from the inlined
// Blob -> object-URL -> anchor -> click -> revoke tails scattered across the
// upload modals and report pages. DOM side-effects only; no behaviour change.

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Triggers a browser download of `blob` named `filename`.
 *
 * Reproduces the appendChild-to-body variant used by the xlsx template
 * downloads (BulkUploadModal, TargetBulkUploadModal, SubMetricsManagePanel):
 * the anchor is appended to `document.body`, clicked, then removed, and the
 * object URL is revoked. `removeChild(a)` and `a.remove()` were both used by
 * the originals — they are equivalent for a body-appended anchor.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Wraps `data` (an axios `response.data` ArrayBuffer/blob payload) in an xlsx
 * Blob and downloads it as `filename`.
 *
 * Extracted from the three template-download handlers, which all built the
 * Blob with the xlsx spreadsheet MIME type and the same anchor tail.
 */
export function downloadXlsx(filename: string, data: ArrayBuffer | Blob): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  downloadBlob(filename, blob);
}

/**
 * Triggers a CSV download using the LEGACY tail shared byte-for-byte by
 * MyApprovalsPage and QuizPerformanceReportPage:
 *   - Blob MIME `text/csv;charset=utf-8;`
 *   - anchor appended to `document.body`, clicked, then removed
 *
 * This deliberately differs from utils/csv.ts `downloadCsv` (which uses the
 * `text/csv` MIME and clicks a detached anchor) — both variants are preserved
 * exactly as their call sites had them.
 */
export function downloadCsvLegacy(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
